/**
 * RouteLink — a real `<a href>` that navigates via the SPA on a plain left
 * click (preventDefault + call onNavigate, letting useUrlSync's state->URL
 * subscription push the new history entry), but falls through to native
 * browser behavior for modified clicks (cmd/ctrl/meta+click, shift+click,
 * middle-click) so users can open the link in a new tab/window (SHA-39).
 *
 * Usage: give it the destination `href` (matching what buildPathname would
 * produce for that resource) and an `onNavigate` callback that updates the
 * relevant Zustand store(s) exactly like the previous onClick handler did.
 */

import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from 'react';

export interface RouteLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'onClick' | 'href'> {
  href: string;
  onNavigate: () => void;
  children: ReactNode;
}

export function RouteLink({ href, onNavigate, children, ...rest }: RouteLinkProps) {
  function handleClick(e: MouseEvent<HTMLAnchorElement>) {
    // Non-primary-button clicks or modifier-clicks (cmd/ctrl/meta/shift) must
    // fall through to native <a> behavior — e.g. opening in a new tab/window,
    // or a middle-click new-tab open. Only intercept the plain left click.
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    e.preventDefault();
    onNavigate();
  }

  return (
    <a href={href} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
}
